import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Lock, LogOut, Users, DollarSign, Mail, Activity, RefreshCw, AlertCircle, BarChart3, MousePointerClick, UserPlus, Trophy, Link2, Copy, Check, Send, TrendingDown,
} from "lucide-react";
import { toast } from "sonner";
import { adminLogin, adminFetch, adminPut, adminPost, adminPatch, getPublicConfig } from "@/lib/api";
import { buildReferralLink } from "@/lib/referrer";
import EmailTemplates from "@/components/admin/EmailTemplates";
import Inquiries from "@/components/admin/Inquiries";
import DiversifiedOutreach from "@/components/admin/DiversifiedOutreach";
import OrgInquiries from "@/components/admin/OrgInquiries";
import SurveyResponses from "@/components/admin/SurveyResponses";
import { TestimonialsAdmin, BuildSharesAdmin } from "@/components/admin/FollowupLists";
import PostLabDrip from "@/components/admin/PostLabDrip";
import SiteContentAdmin from "@/components/admin/SiteContentAdmin";
import RecentPurchases from "@/components/admin/RecentPurchases";

const STORAGE_KEY = "fim_admin_pw";

const STATUSES = [
  { value: "all", label: "All" },
  { value: "payment_pending", label: "Payment Pending" },
  { value: "paid", label: "Paid" },
  { value: "refunded", label: "Refunded" },
  { value: "cancelled", label: "Cancelled" },
  { value: "abandoned_checkout", label: "Abandoned Checkout" },
  { value: "follow_up_needed", label: "Follow-Up Needed" },
  { value: "removed_unsubscribed", label: "Removed / Unsubscribed" },
];

export default function Admin() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [data, setData] = useState({ regs: [], leads: [], stats: null, analytics: null, settings: null, referrers: [], inquiriesSummary: null });
  const [statusFilter, setStatusFilter] = useState("all");
  const [leadsWindow, setLeadsWindow] = useState(null); // null = all, 1 = today, 7 = last 7 days, 30 = last 30 days
  const [seatsInput, setSeatsInput] = useState("");
  const [notifEmailInput, setNotifEmailInput] = useState("");
  const [publicPaymentLink, setPublicPaymentLink] = useState("");

  useEffect(() => {
    if (pw) tryAuth(pw, true);
    getPublicConfig().then((c) => setPublicPaymentLink(c?.payment_link || "")).catch(() => {});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const tryAuth = async (password, silent = false) => {
    setLoading(true);
    try {
      await adminLogin(password);
      sessionStorage.setItem(STORAGE_KEY, password);
      setPw(password);
      setAuthed(true);
      await loadAll(password);
    } catch (e) {
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
      if (!silent) toast.error("Invalid admin password");
    } finally {
      setLoading(false);
    }
  };

  const loadAll = async (password, filter = statusFilter) => {
    try {
      const regsPath = filter && filter !== "all" ? `/admin/registrations?status=${filter}` : "/admin/registrations";
      const [regs, leads, stats, analytics, settings, referrers, inquiriesSummary, emailUsage] = await Promise.all([
        adminFetch(regsPath, password),
        adminFetch("/admin/leads", password),
        adminFetch("/admin/stats", password),
        adminFetch("/admin/analytics", password),
        adminFetch("/admin/settings", password),
        adminFetch("/admin/referrers", password),
        adminFetch("/admin/inquiries", password),
        adminFetch("/admin/email-usage", password),
      ]);
      setData({
        regs: regs.registrations || [],
        leads: leads.leads || [],
        stats, analytics, settings,
        referrers: referrers.referrers || [],
        inquiriesSummary,
        emailUsage,
      });
      if (settings?.settings?.max_seats != null) setSeatsInput(String(settings.settings.max_seats));
      if (settings?.settings?.admin_notification_email != null) setNotifEmailInput(settings.settings.admin_notification_email || "");
    } catch (e) {
      toast.error("Failed to load data");
    }
  };

  const onFilter = (v) => { setStatusFilter(v); loadAll(pw, v); };

  const saveCapacity = async () => {
    const n = parseInt(seatsInput, 10);
    if (isNaN(n) || n < 1) { toast.error("Enter a valid seat count"); return; }
    try {
      await adminPut("/admin/settings", pw, { max_seats: n });
      toast.success(`Class capacity set to ${n}`);
      loadAll(pw);
    } catch { toast.error("Failed to save capacity"); }
  };

  const toggleManualClose = async (value) => {
    try {
      await adminPut("/admin/settings", pw, { manual_closed: value });
      toast.success(value ? "Registration manually closed" : "Registration reopened");
      loadAll(pw);
    } catch { toast.error("Failed to update"); }
  };

  const saveNotifEmail = async () => {
    const trimmed = notifEmailInput.trim();
    if (trimmed && !/\S+@\S+\.\S+/.test(trimmed)) {
      toast.error("Enter a valid email or leave it blank to turn notifications off.");
      return;
    }
    try {
      await adminPut("/admin/settings", pw, { admin_notification_email: trimmed });
      toast.success(trimmed ? `Notifications will go to ${trimmed}.` : "Notifications turned off.");
      loadAll(pw);
    } catch {
      toast.error("Failed to save notification email.");
    }
  };

  const setRegStatus = async (id, status) => {
    try {
      await adminPatch(`/admin/registrations/${id}/status`, pw, { status });
      toast.success("Status updated");
      loadAll(pw);
    } catch { toast.error("Failed to update status"); }
  };

  const sendNurture = async (id, step) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/scheduler/run`, { method: "POST", headers: { "X-Admin-Password": pw } });
      toast.success(`Nurture scheduler fired`);
    } catch { toast.error("Failed"); }
  };

  const runScheduler = async (path, label) => {
    try {
      await adminPost(path, pw);
      toast.success(`${label} fired`);
      loadAll(pw);
    } catch { toast.error(`Failed to run ${label}`); }
  };

  const kickstartNurture = async () => {
    const eligible = leads.filter(
      (l) => l.nurture_active && !l.unsubscribed && !l.converted && l.next_send_at && new Date(l.next_send_at) > new Date()
    ).length;
    if (eligible === 0) {
      toast.info("No paused leads to kickstart — every active lead is already due.");
      return;
    }
    if ((emailUsage?.percent_used ?? 0) >= 90) {
      toast.error(`Resend usage is at ${emailUsage.percent_used}% — bulk sends paused. Wait for midnight UTC reset or override from the API with ?force=true.`);
      return;
    }
    const ok = window.confirm(
      `Kickstart nurture for ${eligible} lead${eligible === 1 ? "" : "s"}?\n\n` +
        `• Batch #1 (up to 40) fires within seconds.\n` +
        `• Remaining batches fire automatically on the hourly schedule.\n` +
        `• Each lead receives Nurture Email #1; subsequent emails follow the compressed pre-lab cadence (1.2 days apart, with countdown emails anchored to June 18 / 21 / 22).\n\n` +
        `Cannot be undone — emails will be sent.`
    );
    if (!ok) return;
    try {
      const res = await adminPost("/admin/leads/kickstart-nurture", pw);
      toast.success(
        `Kickstarted ${res?.kickstarted ?? 0} lead${res?.kickstarted === 1 ? "" : "s"} across ${res?.batches ?? 0} hourly batch${res?.batches === 1 ? "" : "es"}.`
      );
      loadAll(pw);
    } catch (e) {
      toast.error(e?.message || "Failed to kickstart nurture");
    }
  };

  const recomputeNurtureSchedule = async () => {
    const ok = window.confirm(
      "Recompute the nurture schedule for ALL active leads?\n\n" +
        "• Emails 0-5: every 1.2 days (compressed pre-lab cadence)\n" +
        "• Email 6: anchored to June 18 (T-5 countdown)\n" +
        "• Email 7: anchored to June 21 (T-2 countdown)\n" +
        "• Email 8: anchored to June 22 (T-1 final reminder)\n" +
        "• Any email that would fire after the lab is auto-skipped.\n\n" +
        "Existing imports get re-paced into the 12-day window."
    );
    if (!ok) return;
    try {
      const res = await adminPost("/admin/leads/recompute-schedule", pw);
      toast.success(`Schedule recomputed for ${res?.recomputed ?? 0} leads · ${res?.deactivated ?? 0} deactivated (post-lab).`);
      loadAll(pw);
    } catch (e) {
      toast.error(e?.message || "Failed to recompute schedule");
    }
  };

  const sendZoom = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/${id}/send-zoom`, { method: "POST", headers: { "X-Admin-Password": pw } });
      toast.success("Zoom reminder sent");
      loadAll(pw);
    } catch { toast.error("Failed"); }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false); setPw(""); setPwInput("");
  };

  // ---------- LOGIN ----------
  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-white text-ink grid place-items-center px-6">
        <form onSubmit={(e) => { e.preventDefault(); tryAuth(pwInput); }} className="w-full max-w-sm p-8 rounded-2xl bg-bone border border-[var(--line)] shadow-sm">
          <div className="w-12 h-12 rounded-xl bg-brand-soft border border-brand/20 grid place-items-center mb-6">
            <Lock className="text-brand" size={20} />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-[-0.02em] text-ink">Admin access</h1>
          <p className="text-sm text-ink/65 mt-2">Enter the admin password.</p>
          <div className="mt-6 space-y-2">
            <Label htmlFor="pw" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">Password</Label>
            <Input id="pw" type="password" data-testid="admin-password-input" value={pwInput} onChange={(e) => setPwInput(e.target.value)} className="bg-white border border-[var(--line)] text-ink h-11 focus-visible:ring-brand focus-visible:ring-offset-0" autoFocus />
          </div>
          <Button type="submit" data-testid="admin-login-submit" disabled={loading} className="w-full mt-6 btn-ink rounded-full h-11 font-medium">
            {loading ? <Loader2 className="animate-spin" size={16} /> : "Sign In"}
          </Button>
        </form>
      </div>
    );
  }

  // ---------- DASHBOARD ----------
  const { stats, analytics, settings, regs, leads, referrers, inquiriesSummary, emailUsage } = data;
  const newInquiries = inquiriesSummary?.new_count || 0;
  const cap = stats?.capacity || settings?.capacity || {};
  const remaining = cap?.remaining ?? "—";
  const manualClosed = !!settings?.settings?.manual_closed;

  // Client-side date filter for the Leads tab. `null` = show all.
  const newLeadsWindowDays = leadsWindow;
  const cutoffMs = newLeadsWindowDays ? Date.now() - newLeadsWindowDays * 24 * 60 * 60 * 1000 : 0;
  const filteredLeads = newLeadsWindowDays
    ? (leads || []).filter((l) => {
        if (!l.created_at) return false;
        const t = new Date(l.created_at).getTime();
        return !isNaN(t) && t >= cutoffMs;
      })
    : leads || [];
  const newLeadsToday = (leads || []).filter((l) => {
    if (!l.created_at) return false;
    const t = new Date(l.created_at).getTime();
    return !isNaN(t) && t >= Date.now() - 24 * 60 * 60 * 1000;
  }).length;
  const newLeads7d = (leads || []).filter((l) => {
    if (!l.created_at) return false;
    const t = new Date(l.created_at).getTime();
    return !isNaN(t) && t >= Date.now() - 7 * 24 * 60 * 60 * 1000;
  }).length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-white text-ink">
      <header className="border-b border-[var(--line)] bg-white/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-ink grid place-items-center text-[10px] font-bold text-white">F</div>
            <div className="font-display font-bold tracking-tight">Admin Console</div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="admin-refresh" onClick={() => loadAll(pw)} className="btn-outline-ink rounded-full h-9 px-4 text-sm"><RefreshCw size={13} className="mr-2" />Refresh</Button>
            <Button variant="outline" data-testid="admin-logout-btn" onClick={logout} className="btn-outline-ink rounded-full h-9 px-4 text-sm"><LogOut size={13} className="mr-2" />Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        {/* Capacity banner */}
        <div className={`rounded-2xl border p-6 ${cap?.is_closed ? "border-[#B85427] bg-[#FDF1EA]" : "border-[var(--line)] bg-bone"}`}>
          <div className="flex flex-wrap items-center gap-6 justify-between">
            <div className="flex items-center gap-3">
              {cap?.is_closed ? <AlertCircle size={20} className="text-[#B85427]" /> : <Activity size={20} className="text-brand" />}
              <div>
                <div className="font-display text-lg">{cap?.is_closed ? cap?.cta_label : "Enrollment Open"}</div>
                <div className="text-sm text-ink/65">
                  Paid: <strong className="text-ink">{cap?.paid_count ?? 0}</strong> &middot;
                  Capacity: <strong className="text-ink"> {cap?.max_seats ?? "—"}</strong> &middot;
                  Remaining: <strong className="text-ink"> {remaining}</strong>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-end gap-2">
                <div>
                  <Label htmlFor="seats" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">Class capacity</Label>
                  <Input id="seats" data-testid="admin-seats-input" type="number" min={1} value={seatsInput} onChange={(e) => setSeatsInput(e.target.value)} className="w-28 h-10 mt-1.5 bg-white border border-[var(--line)] text-ink focus-visible:ring-brand focus-visible:ring-offset-0" />
                </div>
                <Button onClick={saveCapacity} data-testid="admin-save-seats" className="btn-ink rounded-full h-10 px-5">Save</Button>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-ink/70">Manually close registration</span>
                <Switch checked={manualClosed} onCheckedChange={toggleManualClose} data-testid="admin-manual-close-toggle" />
              </div>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t border-[var(--line)]/60 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[260px]">
              <Label htmlFor="notif_email" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">
                Notify me when a new lead, registration, or inquiry comes in
              </Label>
              <Input
                id="notif_email"
                type="email"
                data-testid="admin-notif-email"
                value={notifEmailInput}
                onChange={(e) => setNotifEmailInput(e.target.value)}
                placeholder="info@itsthefreedominme.com — leave blank to turn off"
                className="h-10 mt-1.5 bg-white border border-[var(--line)] text-ink focus-visible:ring-brand focus-visible:ring-offset-0"
              />
              <p className="text-xs text-ink/55 mt-1.5 leading-relaxed">
                We&apos;ll ping you the moment a new <strong>lead</strong>, <strong>registration</strong>, or <strong>contact inquiry</strong> comes in &mdash; with their name, email, and a one-click path to your admin dashboard so you can fire a personal Gmail follow-up. Leave blank to turn off and just check the dashboard.
              </p>
            </div>
            <Button onClick={saveNotifEmail} data-testid="admin-save-notif" className="btn-ink rounded-full h-10 px-5">
              Save
            </Button>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid sm:grid-cols-4 gap-4">
          <Stat icon={Users} label="Total Registrations" value={stats?.total_registrations ?? "—"} />
          <Stat icon={DollarSign} label="Paid" value={stats?.by_status?.paid ?? 0} accent />
          <Stat icon={Mail} label="Active Lead Nurture" value={stats?.leads_active_nurture ?? "—"} />
          <Stat icon={Activity} label="Remaining Seats" value={remaining} />
        </div>

        {/* Conversion Funnel */}
        <ConversionFunnel stats={stats} analytics={analytics} />

        {/* Resend email usage today */}
        <EmailUsageCard usage={emailUsage} />

        {/* Analytics */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={18} className="text-brand" />
            <h2 className="font-display text-xl tracking-[-0.02em]">Traffic & funnel</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Mini label="Page views" value={analytics?.page_views ?? 0} />
            <Mini label="Unique visitors" value={analytics?.unique_visitors ?? 0} />
            <Mini label="Form starts" value={analytics?.form_starts ?? 0} />
            <Mini label="Form completes" value={analytics?.form_completes ?? 0} />
            <Mini label="Checkout starts" value={analytics?.checkout_starts ?? 0} />
            <Mini label="Checkout completes" value={analytics?.checkout_completes ?? 0} />
            <Mini label="Abandoned checkouts" value={analytics?.abandoned_checkouts ?? 0} />
            <Mini label="Conversion rate" value={`${analytics?.conversion_rate_pct ?? 0}%`} accent />
          </div>
          {analytics?.by_cta && Object.keys(analytics.by_cta).length > 0 && (
            <div className="mt-5 grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(analytics.by_cta).map(([k, v]) => (
                <div key={k} className="rounded-xl border border-[var(--line)] bg-bone p-4 flex items-center gap-3">
                  <MousePointerClick size={14} className="text-brand" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-ink/60 truncate">{k}</div>
                    <div className="font-display text-xl text-ink">{v}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Referral Link Generator */}
        <ReferralLinkBuilder />

        {/* Top Referrers */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={18} className="text-brand" />
            <h2 className="font-display text-xl tracking-[-0.02em]">Top referrers</h2>
            <span className="text-xs text-ink/55 font-mono-spec uppercase tracking-wider ml-1">
              Ranked by paid &middot; then total mentions
            </span>
          </div>
          {referrers && referrers.length > 0 ? (
            <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-bone overflow-x-auto" data-testid="referrers-card">
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--line)] hover:bg-transparent">
                    <TableHead className="text-ink/60 w-12">#</TableHead>
                    <TableHead className="text-ink/60">Referrer</TableHead>
                    <TableHead className="text-ink/60 text-right">Leads</TableHead>
                    <TableHead className="text-ink/60 text-right">Registrations</TableHead>
                    <TableHead className="text-ink/60 text-right">Paid</TableHead>
                    <TableHead className="text-ink/60 text-right">Total mentions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrers.map((r, i) => (
                    <TableRow key={`${r.name}-${i}`} data-testid={`referrer-row-${i}`} className="border-[var(--line)] hover:bg-white">
                      <TableCell className="font-mono-spec text-xs text-ink/55">
                        {i === 0 ? <span className="text-brand font-bold">01</span>
                          : i === 1 ? <span className="text-ink font-bold">02</span>
                          : i === 2 ? <span className="text-ink/80 font-bold">03</span>
                          : String(i + 1).padStart(2, "0")}
                      </TableCell>
                      <TableCell className="font-medium text-ink">{r.name || "—"}</TableCell>
                      <TableCell className="text-right text-ink/75 font-mono-spec text-sm">{r.leads}</TableCell>
                      <TableCell className="text-right text-ink/75 font-mono-spec text-sm">{r.registrations}</TableCell>
                      <TableCell className="text-right font-mono-spec text-sm">
                        {r.paid > 0
                          ? <span className="text-emerald-700 font-bold">{r.paid}</span>
                          : <span className="text-ink/45">0</span>}
                      </TableCell>
                      <TableCell className="text-right text-ink font-display text-lg">{r.total}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div data-testid="referrers-empty" className="rounded-2xl border border-dashed border-[var(--line)] bg-bone p-8 text-center text-ink/55 text-sm">
              No referrals yet. Once someone fills in &ldquo;Referred By&rdquo; on a form &mdash; or you add a lead manually with a referrer &mdash; they&rsquo;ll show up here.
            </div>
          )}
        </div>

        {/* Tabs: Registrations / Leads */}
        <Tabs defaultValue="registrations" className="w-full">
          <TabsList className="bg-bone border border-[var(--line)]">
            <TabsTrigger value="registrations" data-testid="tab-registrations">Registrations</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">
              Inquiries{newInquiries > 0 ? ` · ${newInquiries} new` : ""}
            </TabsTrigger>
            <TabsTrigger value="diversified" data-testid="tab-diversified">Diversified Outreach</TabsTrigger>
            <TabsTrigger value="org-inquiries" data-testid="tab-org-inquiries">Org Inquiries</TabsTrigger>
            <TabsTrigger value="survey-responses" data-testid="tab-survey-responses">Survey Responses</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="build-shares" data-testid="tab-build-shares">Build Shares</TabsTrigger>
            <TabsTrigger value="post-lab-drip" data-testid="tab-post-lab-drip">Post-Lab Drip</TabsTrigger>
            <TabsTrigger value="purchases" data-testid="tab-purchases">Recent Purchases</TabsTrigger>
            <TabsTrigger value="site-content" data-testid="tab-site-content">Site Content</TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">Emails</TabsTrigger>
          </TabsList>

          <TabsContent value="registrations" className="mt-5">
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-3">
                <Label className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-medium font-mono">Filter by status</Label>
                <Select value={statusFilter} onValueChange={onFilter}>
                  <SelectTrigger className="w-56 bg-white border border-[var(--line)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => runScheduler("/admin/scheduler/run", "Lead nurture")} className="btn-outline-ink rounded-full h-9 px-4 text-sm">Run Lead Nurture</Button>
                <Button variant="outline" onClick={() => runScheduler("/admin/abandoned/run", "Abandoned sequence")} className="btn-outline-ink rounded-full h-9 px-4 text-sm">Run Abandoned</Button>
                <Button variant="outline" onClick={() => runScheduler("/admin/zoom-reminder/run", "Zoom reminder")} className="btn-outline-ink rounded-full h-9 px-4 text-sm">Run Zoom Reminder</Button>
              </div>
            </div>

            <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-bone overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--line)] hover:bg-transparent">
                    <TableHead className="text-ink/60">Name</TableHead>
                    <TableHead className="text-ink/60">Email</TableHead>
                    <TableHead className="text-ink/60">Phone</TableHead>
                    <TableHead className="text-ink/60">Interest</TableHead>
                    <TableHead className="text-ink/60">Referred By</TableHead>
                    <TableHead className="text-ink/60">Status</TableHead>
                    <TableHead className="text-ink/60">Abandoned step</TableHead>
                    <TableHead className="text-ink/60 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {regs.length === 0 && (
                    <TableRow className="border-[var(--line)] hover:bg-transparent">
                      <TableCell colSpan={8} className="text-center text-ink/50 py-10">No registrations match this filter.</TableCell>
                    </TableRow>
                  )}
                  {regs.map((r) => (
                    <TableRow key={r.id} data-testid={`reg-row-${r.id}`} className="border-[var(--line)] hover:bg-white">
                      <TableCell className="font-medium text-ink">{r.first_name} {r.last_name}</TableCell>
                      <TableCell className="text-ink/75">{r.email}</TableCell>
                      <TableCell className="text-ink/65">{r.phone}</TableCell>
                      <TableCell className="text-ink/65 capitalize">{r.interest?.replace(/_/g, " ")}</TableCell>
                      <TableCell className="text-ink/65 text-xs" data-testid={`reg-referred-by-${r.id}`}>{r.referred_by || "—"}</TableCell>
                      <TableCell>
                        <StatusBadge status={r.status} />
                      </TableCell>
                      <TableCell className="text-ink/65 font-mono-spec text-xs">
                        {r.abandoned_step || 0}/{4}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-2">
                          <Select value="" onValueChange={(v) => v && setRegStatus(r.id, v)}>
                            <SelectTrigger data-testid={`reg-status-select-${r.id}`} className="w-40 h-8 bg-white border border-[var(--line)] text-xs"><SelectValue placeholder="Set status..." /></SelectTrigger>
                            <SelectContent>
                              {STATUSES.filter((s) => s.value !== "all").map((s) => (
                                <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="outline" onClick={() => sendZoom(r.id)} className="btn-outline-ink rounded-full h-8 px-3 text-xs">Send Zoom</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="leads" className="mt-5">
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-sm text-ink/65">
                  Leads enter the 9-email nurture sequence (~3/week). Manually added leads skip the &ldquo;thanks for opting in&rdquo; email.
                </p>
                {(newLeadsToday > 0 || newLeads7d > 0) && (
                  <div className="flex items-center gap-2 text-xs" data-testid="new-leads-summary">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-soft border border-brand/20 text-brand font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                      {newLeadsToday} new today
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[var(--line)] text-ink/65">
                      {newLeads7d} this week
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={recomputeNurtureSchedule}
                  data-testid="recompute-schedule-btn"
                  className="btn-outline-ink rounded-full h-9 px-4 text-sm"
                >
                  <RefreshCw size={13} className="mr-1.5" />
                  Recompute Schedule
                </Button>
                <Button
                  variant="outline"
                  onClick={kickstartNurture}
                  disabled={(emailUsage?.percent_used ?? 0) >= 90}
                  data-testid="kickstart-nurture-btn"
                  title={(emailUsage?.percent_used ?? 0) >= 90 ? `Paused — Resend at ${emailUsage.percent_used}%` : ""}
                  className="btn-outline-ink rounded-full h-9 px-4 text-sm border-brand/40 text-brand hover:bg-brand-soft disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Send size={13} className="mr-1.5" />
                  Kickstart Nurture Now
                </Button>
                <BulkImportDialog pw={pw} onImported={() => loadAll(pw)} />
                <AddLeadDialog pw={pw} onAdded={() => loadAll(pw)} />
              </div>
            </div>

            {/* Date-range filter chips */}
            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="leads-filter-chips">
              <span className="text-[10px] uppercase tracking-[0.16em] text-ink/50 font-mono mr-1">Show</span>
              {[
                { v: null, l: "All" },
                { v: 1, l: "Today" },
                { v: 7, l: "Last 7 days" },
                { v: 30, l: "Last 30 days" },
              ].map((o) => (
                <button
                  key={String(o.v)}
                  onClick={() => setLeadsWindow(o.v)}
                  data-testid={`leads-filter-${o.l.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    leadsWindow === o.v
                      ? "bg-ink text-white border-ink"
                      : "bg-white text-ink/70 border-[var(--line)] hover:bg-bone"
                  }`}
                >
                  {o.l}
                </button>
              ))}
              {leadsWindow !== null && (
                <span className="text-xs text-ink/55 ml-2">
                  Showing <strong className="text-ink">{filteredLeads.length}</strong> of {leads?.length || 0}
                </span>
              )}
            </div>

            <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-bone overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--line)] hover:bg-transparent">
                    <TableHead className="text-ink/60">First Name</TableHead>
                    <TableHead className="text-ink/60">Email</TableHead>
                    <TableHead className="text-ink/60">Phone</TableHead>
                    <TableHead className="text-ink/60">Referred By</TableHead>
                    <TableHead className="text-ink/60">Nurture step</TableHead>
                    <TableHead className="text-ink/60">Status</TableHead>
                    <TableHead className="text-ink/60">Next send</TableHead>
                    <TableHead className="text-ink/60 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.length === 0 && (
                    <TableRow className="border-[var(--line)] hover:bg-transparent">
                      <TableCell colSpan={8} className="text-center text-ink/50 py-10">
                        {leadsWindow !== null
                          ? `No leads in this window. ${leads?.length || 0} total leads — try widening the filter.`
                          : "No leads yet. They'll appear when someone submits the \"Send Me the Details\" form."}
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredLeads.map((l) => (
                    <TableRow key={l.id} className="border-[var(--line)] hover:bg-white">
                      <TableCell className="text-ink">{l.first_name}</TableCell>
                      <TableCell className="text-ink/75">{l.email}</TableCell>
                      <TableCell className="text-ink/65">{l.phone || "—"}</TableCell>
                      <TableCell className="text-ink/65 text-xs" data-testid={`lead-referred-by-${l.id}`}>{l.referred_by || "—"}</TableCell>
                      <TableCell className="text-ink/65 font-mono-spec text-xs">{l.nurture_step || 0}/9</TableCell>
                      <TableCell>
                        {l.unsubscribed ? <Badge variant="outline" className="border-[#B85427]/40 text-[#B85427] bg-[#FDF1EA]">Unsubscribed</Badge>
                          : l.converted ? <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">Converted</Badge>
                          : l.nurture_active ? <Badge variant="outline" className="border-brand/40 text-brand bg-brand-soft">Active</Badge>
                          : <Badge variant="outline" className="border-[var(--line)] text-ink/60 bg-white">Idle</Badge>}
                      </TableCell>
                      <TableCell className="text-ink/65 text-xs">{l.next_send_at ? new Date(l.next_send_at).toLocaleString() : "—"}</TableCell>
                      <TableCell className="text-right">
                        <ManualEmailButton lead={l} paymentLink={publicPaymentLink} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="emails" className="mt-5">
            <EmailTemplates pw={pw} />
          </TabsContent>

          <TabsContent value="inquiries" className="mt-5">
            <Inquiries pw={pw} />
          </TabsContent>

          <TabsContent value="diversified" className="mt-5">
            <DiversifiedOutreach pw={pw} emailUsage={emailUsage} />
          </TabsContent>

          <TabsContent value="org-inquiries" className="mt-5">
            <OrgInquiries pw={pw} emailUsage={emailUsage} />
          </TabsContent>

          <TabsContent value="survey-responses" className="mt-5">
            <SurveyResponses pw={pw} />
          </TabsContent>

          <TabsContent value="testimonials" className="mt-5">
            <TestimonialsAdmin pw={pw} />
          </TabsContent>

          <TabsContent value="build-shares" className="mt-5">
            <BuildSharesAdmin pw={pw} />
          </TabsContent>

          <TabsContent value="post-lab-drip" className="mt-5">
            <PostLabDrip pw={pw} />
          </TabsContent>

          <TabsContent value="purchases" className="mt-5">
            <RecentPurchases pw={pw} />
          </TabsContent>

          <TabsContent value="site-content" className="mt-5">
            <SiteContentAdmin pw={pw} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-2xl p-6 bg-bone border ${accent ? "border-brand/40" : "border-[var(--line)]"}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec">{label}</div>
        <Icon size={15} className={accent ? "text-brand" : "text-ink/40"} />
      </div>
      <div className="font-display text-4xl tracking-[-0.03em] text-ink">{value}</div>
    </div>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div className={`rounded-xl p-4 bg-bone border ${accent ? "border-brand/40" : "border-[var(--line)]"}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec">{label}</div>
      <div className={`font-display text-2xl mt-1 tracking-[-0.025em] ${accent ? "text-brand" : "text-ink"}`}>{value}</div>
    </div>
  );
}

function EmailUsageCard({ usage }) {
  if (!usage) return null;
  const sent = usage.sent || 0;
  const limit = usage.limit || 0;
  const pct = limit > 0 ? Math.min(100, (sent / limit) * 100) : 0;
  // Color bands: <70% green, 70-90% amber, >=90% red.
  const fillColor = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-brand";
  const labelColor = pct >= 90 ? "text-red-600" : pct >= 70 ? "text-amber-700" : "text-brand";
  const status =
    pct >= 100 ? "Daily quota reached — Resend will block new sends until midnight UTC."
    : pct >= 90 ? "Approaching daily limit. Pause kickstarts."
    : pct >= 70 ? "Watch this. Big kickstart batches could hit the cap."
    : "Plenty of headroom.";
  return (
    <div data-testid="email-usage-card" className="rounded-2xl border border-[var(--line)] bg-bone p-5">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Mail size={15} className="text-brand" />
            <h2 className="font-display text-xl tracking-[-0.02em]">Resend usage today</h2>
          </div>
          <p className="text-xs text-ink/55 font-mono-spec uppercase tracking-wider">
            {usage.date}{usage.failed > 0 ? ` · ${usage.failed} failures` : ""}
            {usage.rate_limit_hits_today > 0 ? ` · ${usage.rate_limit_hits_today} rate-limited` : ""}
            {usage.quota_hits_today > 0 ? ` · ${usage.quota_hits_today} quota errors` : ""}
          </p>
        </div>
        <div className="text-right">
          <div className={`font-display text-3xl tracking-[-0.025em] ${labelColor}`} data-testid="email-usage-count">
            {sent.toLocaleString()} <span className="text-ink/40 text-xl">/ {limit.toLocaleString()}</span>
          </div>
          <div className="text-[11px] text-ink/55 font-mono-spec uppercase tracking-wider mt-0.5">
            {usage.remaining.toLocaleString()} remaining
          </div>
        </div>
      </div>
      <div className="h-2 rounded-full bg-white border border-[var(--line)] overflow-hidden">
        <div
          className={`h-full ${fillColor} transition-all duration-500`}
          style={{ width: `${pct}%` }}
          data-testid="email-usage-bar"
        />
      </div>
      <p className={`text-xs mt-2 ${labelColor}`}>{status}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: { cls: "border-emerald-300 text-emerald-700 bg-emerald-50", label: "Paid" },
    payment_pending: { cls: "border-amber-300 text-amber-700 bg-amber-50", label: "Payment Pending" },
    refunded: { cls: "border-zinc-300 text-zinc-600 bg-zinc-100", label: "Refunded" },
    cancelled: { cls: "border-zinc-400 text-zinc-700 bg-zinc-50", label: "Cancelled" },
    started_registration: { cls: "border-amber-300 text-amber-700 bg-amber-50", label: "Payment Pending" },
    abandoned_checkout: { cls: "border-[#B85427]/40 text-[#B85427] bg-[#FDF1EA]", label: "Abandoned" },
    follow_up_needed: { cls: "border-brand/40 text-brand bg-brand-soft", label: "Follow-up" },
    removed_unsubscribed: { cls: "border-zinc-300 text-zinc-600 bg-zinc-100", label: "Unsubscribed" },
  };
  const m = map[status] || { cls: "border-[var(--line)] text-ink/60 bg-white", label: status };
  return <Badge variant="outline" className={`${m.cls} text-xs`}>{m.label}</Badge>;
}

function AddLeadDialog({ pw, onAdded }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ first_name: "", email: "", phone: "", referred_by: "", send_intro: true });

  const reset = () => setForm({ first_name: "", email: "", phone: "", referred_by: "", send_intro: true });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) {
      toast.error("First name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await adminPost("/admin/leads", pw, form);
      const intro = res?.intro_sent ? " Intro email sent." : "";
      toast.success((res?.created === false ? "Lead re-activated." : "Lead added.") + intro);
      reset();
      setOpen(false);
      onAdded?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to add lead.";
      toast.error(typeof detail === "string" ? detail : "Failed to add lead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="admin-add-lead-btn" className="btn-ink rounded-full h-9 px-4 text-sm">
          <UserPlus size={14} className="mr-2" />
          Add Lead
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[-0.025em]">Add a lead manually</DialogTitle>
          <DialogDescription className="text-ink/65">
            For people you met offline, on a call, or through a referral. They&apos;ll enter the 9-email nurture sequence (~3/week).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="al_first" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">First Name *</Label>
            <Input
              id="al_first"
              data-testid="add-lead-first"
              value={form.first_name}
              onChange={(e) => setForm((s) => ({ ...s, first_name: e.target.value }))}
              className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="al_email" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Email *</Label>
            <Input
              id="al_email"
              type="email"
              data-testid="add-lead-email"
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
            />
          </div>
          <div>
            <Label htmlFor="al_phone" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Phone (optional)</Label>
            <Input
              id="al_phone"
              type="tel"
              data-testid="add-lead-phone"
              value={form.phone}
              onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
              className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
            />
          </div>
          <div>
            <Label htmlFor="al_referred_by" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Referred By (optional)</Label>
            <Input
              id="al_referred_by"
              data-testid="add-lead-referred-by"
              value={form.referred_by}
              onChange={(e) => setForm((s) => ({ ...s, referred_by: e.target.value }))}
              placeholder="Name of the person who referred them"
              className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
            />
          </div>
          <label className="flex items-start gap-3 cursor-pointer pt-2 select-none" data-testid="add-lead-send-intro-label">
            <input
              type="checkbox"
              data-testid="add-lead-send-intro"
              checked={form.send_intro}
              onChange={(e) => setForm((s) => ({ ...s, send_intro: e.target.checked }))}
              className="mt-0.5 w-4 h-4 accent-brand cursor-pointer"
            />
            <span className="text-sm text-ink/80 leading-snug">
              Send them the &ldquo;Here are the details&rdquo; intro email right now.
              <span className="block text-xs text-ink/55 mt-0.5">Uncheck to add silently. Nurture sequence still starts ~2 days later either way.</span>
            </span>
          </label>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="btn-outline-ink rounded-full h-10 px-5">
              Cancel
            </Button>
            <Button type="submit" data-testid="add-lead-submit" disabled={submitting} className="btn-ink rounded-full h-10 px-5">
              {submitting ? <Loader2 className="animate-spin" size={14} /> : "Add Lead"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const RECENT_KEY = "fim_admin_recent_referrals";

function ReferralLinkBuilder() {
  const [name, setName] = useState("");
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
  });
  const [copiedLink, setCopiedLink] = useState("");

  const origin = window.location.origin;
  const link = name.trim() ? buildReferralLink(origin, name) : "";

  const persistRecent = (arr) => {
    try { sessionStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, 8))); } catch { /* ignore */ }
  };

  const copy = async (url, label) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(url);
      toast.success(`Link copied — share it with ${label || "your referrer"}.`);
      setTimeout(() => setCopiedLink(""), 1800);
      // Add to recent
      const next = [{ name: label || name.trim(), link: url, ts: Date.now() }, ...recent.filter((r) => r.link !== url)];
      setRecent(next);
      persistRecent(next);
    } catch {
      toast.error("Couldn't copy. Long-press / right-click the link to copy manually.");
    }
  };

  const clearRecent = () => {
    setRecent([]);
    persistRecent([]);
  };

  return (
    <div data-testid="referral-link-builder">
      <div className="flex items-center gap-2 mb-4">
        <Link2 size={18} className="text-brand" />
        <h2 className="font-display text-xl tracking-[-0.02em]">Generate a referral link</h2>
        <span className="text-xs text-ink/55 font-mono-spec uppercase tracking-wider ml-1">
          Pre-fills the &ldquo;Referred By&rdquo; field automatically
        </span>
      </div>

      <div className="rounded-2xl border border-[var(--line)] bg-bone p-6">
        <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label htmlFor="ref_name" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">
              Referrer&apos;s name
            </Label>
            <Input
              id="ref_name"
              data-testid="ref-builder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Donja Williams"
              className="bg-white border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
            />
          </div>
          <Button
            type="button"
            data-testid="ref-builder-copy"
            disabled={!link}
            onClick={() => copy(link, name.trim())}
            className="btn-ink rounded-full h-11 px-5"
          >
            {copiedLink === link ? <><Check size={14} className="mr-2" /> Copied</> : <><Copy size={14} className="mr-2" /> Copy link</>}
          </Button>
        </div>

        {link && (
          <div className="mt-4 rounded-xl bg-white border border-[var(--line)] p-3 flex items-center gap-3 overflow-hidden" data-testid="ref-builder-preview">
            <Link2 size={14} className="text-brand shrink-0" />
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink text-sm font-mono-spec truncate hover:underline"
              title={link}
            >
              {link}
            </a>
          </div>
        )}

        <p className="text-xs text-ink/55 mt-4 leading-relaxed">
          Share this link with the person referring people to the lab. Anyone who opens it will have <strong>{name.trim() || "their name"}</strong> auto-filled on the Lead Capture and Registration forms &mdash; you&apos;ll see them roll up in the leaderboard below.
        </p>

        {recent.length > 0 && (
          <div className="mt-6 border-t border-[var(--line)] pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono-spec">Recently copied</div>
              <button
                type="button"
                onClick={clearRecent}
                className="text-[11px] uppercase tracking-[0.18em] text-ink/45 hover:text-ink font-mono-spec"
                data-testid="ref-builder-clear-recent"
              >
                Clear
              </button>
            </div>
            <div className="space-y-1.5">
              {recent.map((r, i) => (
                <div key={`${r.link}-${i}`} data-testid={`ref-builder-recent-${i}`} className="flex items-center justify-between gap-3 rounded-lg bg-white border border-[var(--line)] px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-ink font-medium truncate">{r.name}</div>
                    <div className="text-xs text-ink/55 font-mono-spec truncate" title={r.link}>{r.link}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => copy(r.link, r.name)}
                    className="text-xs font-mono-spec uppercase tracking-wider text-brand hover:underline shrink-0"
                  >
                    {copiedLink === r.link ? "Copied" : "Copy"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



function ManualEmailButton({ lead, paymentLink }) {
  const [open, setOpen] = useState(false);
  const first = lead?.first_name?.trim() || "there";
  const link = paymentLink || "https://donjawilliams.com/#register";
  const defaultSubject = "Your idea has waited long enough — here are the details";
  const defaultBody = `Hi ${first},

You reached out about the AI Website & App Builder Live Build Lab — here are the details:

• What: Live 2-hour online build lab
• When: June 23, 2026 · 7:00–9:00 PM EST (live online)
• Price: $150 — one-time, all-in
• Who it's for: Non-technical leaders, entrepreneurs, nonprofit founders, consultants, coaches

Seats are limited and this is the introductory cohort — it will never be this price again.

Save your seat: ${link}

Questions? Just reply to this email.

— Donja Williams
Founder & CEO, Freedom In Me LLC`;

  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);

  const mailto = `mailto:${encodeURIComponent(lead?.email || "")}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const copyBody = async () => {
    try {
      await navigator.clipboard.writeText(body);
      toast.success("Copied. Paste into your email.");
    } catch {
      toast.error("Couldn't copy. Long-press / right-click to copy manually.");
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        data-testid={`lead-manual-email-${lead?.id}`}
        onClick={() => setOpen(true)}
        className="btn-outline-ink rounded-full h-8 px-3 text-xs"
      >
        <Send size={11} className="mr-1.5" /> Email manually
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-2xl" data-testid="manual-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-[-0.025em]">Email {lead?.first_name || "this lead"} from your Gmail</DialogTitle>
            <DialogDescription className="text-ink/65">
              Sends from <strong>your real Gmail account</strong> &mdash; bypasses Resend/DNS entirely and lands in their inbox every time. Edit if you want, then click &ldquo;Open in mail app&rdquo; to compose with one click.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">To</Label>
              <Input value={lead?.email || ""} readOnly className="bg-bone border border-[var(--line)] text-ink h-11 mt-1.5" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Subject</Label>
              <Input
                data-testid="manual-email-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="bg-white border border-[var(--line)] text-ink h-11 mt-1.5 focus-visible:ring-brand focus-visible:ring-offset-0"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Body</Label>
              <Textarea
                data-testid="manual-email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={14}
                className="bg-white border border-[var(--line)] text-ink mt-1.5 text-[14px] leading-relaxed focus-visible:ring-brand focus-visible:ring-offset-0"
              />
            </div>
          </div>

          <DialogFooter className="pt-3 gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              data-testid="manual-email-copy"
              onClick={copyBody}
              className="btn-outline-ink rounded-full h-10 px-4"
            >
              <Copy size={13} className="mr-1.5" /> Copy body
            </Button>
            <a href={mailto} target="_blank" rel="noopener noreferrer" data-testid="manual-email-open" className="inline-flex">
              <Button type="button" className="btn-ink rounded-full h-10 px-5">
                <Send size={13} className="mr-1.5" /> Open in mail app
              </Button>
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function BulkImportDialog({ pw, onImported }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sendIntro, setSendIntro] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setText(""); setSendIntro(false); setResult(null); };

  const runImport = async () => {
    if (!text.trim()) {
      toast.error("Paste your contact list first.");
      return;
    }
    setBusy(true);
    try {
      const res = await adminPost("/admin/leads/bulk-import", pw, {
        contacts_text: text,
        send_intro: sendIntro,
      });
      setResult(res);
      if (res.ok && res.created > 0) {
        toast.success(`${res.created} new leads added.`);
        onImported?.();
      } else if (!res.ok) {
        toast.error(res.message || "Nothing to import.");
      } else {
        toast.success("All contacts already in your funnel — nothing new to import.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="bulk-import-trigger" className="btn-outline-ink rounded-full h-10 px-4">
          <UserPlus size={13} className="mr-1.5" /> Bulk import
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[-0.025em]">Bulk import contacts to the funnel</DialogTitle>
          <DialogDescription className="text-ink/65">
            Paste your list below. We accept any combination of <code className="px-1 py-0.5 bg-bone rounded text-xs">Name &lt;email&gt;</code>, <code className="px-1 py-0.5 bg-bone rounded text-xs">&quot;Quoted Name&quot; &lt;email&gt;</code>, or bare <code className="px-1 py-0.5 bg-bone rounded text-xs">email@domain.com</code> &mdash; separated by commas, line breaks, or both. Duplicates and unsubscribed contacts are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 pt-2">
            <div>
              <Label htmlFor="bulk_text" className="text-[11px] uppercase tracking-[0.18em] text-ink/60 font-mono">Contacts</Label>
              <Textarea
                id="bulk_text"
                data-testid="bulk-import-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={12}
                placeholder={`Tiffany Daniels <td1808@aol.com>, Rebecca Hinson <info@example.com>, plain@email.com ...`}
                className="bg-bone border border-[var(--line)] text-ink mt-1.5 text-[13px] leading-relaxed font-mono focus-visible:ring-brand focus-visible:ring-offset-0"
              />
            </div>

            <div className="rounded-xl border border-[var(--line)] bg-bone p-4 flex items-start gap-3">
              <div className="pt-0.5">
                <Switch
                  data-testid="bulk-import-send-intro"
                  checked={sendIntro}
                  onCheckedChange={setSendIntro}
                />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-ink">Send the intro email now</div>
                <p className="text-xs text-ink/60 leading-relaxed mt-0.5">
                  <strong>Recommended OFF for big lists.</strong> Bulk-sending 50+ emails at once from a new sender domain can trigger Gmail rate limits or spam complaints. Leave OFF and follow up personally with the &ldquo;Email manually&rdquo; button on each lead row &mdash; way better deliverability + warmer first touch. Either way they enter the 9-email nurture sequence (~3/week) starting 2 days from now.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2" data-testid="bulk-import-result">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <div className="font-display text-3xl tracking-[-0.025em] text-emerald-800">
                {result.created} <span className="font-serif-em italic text-emerald-700 font-light">new leads</span> added
              </div>
              <p className="text-sm text-emerald-900/80 mt-1">
                They&apos;re now in your nurture funnel (starting ~2 days from now). {result.intro_emails_queued > 0 ? `${result.intro_emails_queued} intro emails queued in the background.` : "No intro emails sent — follow up personally via the Email manually button on each row."}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="rounded-xl bg-bone border border-[var(--line)] p-3">
                <div className="text-2xl font-display text-ink">{result.parsed_total}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink/55 font-mono-spec mt-1">Parsed</div>
              </div>
              <div className="rounded-xl bg-bone border border-[var(--line)] p-3">
                <div className="text-2xl font-display text-ink/65">{result.skipped_duplicates}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink/55 font-mono-spec mt-1">Already leads</div>
              </div>
              <div className="rounded-xl bg-bone border border-[var(--line)] p-3">
                <div className="text-2xl font-display text-ink/65">{result.skipped_registrants}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink/55 font-mono-spec mt-1">Already registered</div>
              </div>
              <div className="rounded-xl bg-bone border border-[var(--line)] p-3">
                <div className="text-2xl font-display text-ink/65">{result.skipped_unsubscribed}</div>
                <div className="text-[10px] uppercase tracking-wider text-ink/55 font-mono-spec mt-1">Unsubscribed</div>
              </div>
            </div>

            {result.invalid && result.invalid.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="text-sm font-medium text-amber-900 mb-2">{result.invalid.length} lines couldn&apos;t be parsed:</div>
                <ul className="text-xs text-amber-800 space-y-1 max-h-32 overflow-y-auto font-mono">
                  {result.invalid.slice(0, 20).map((row, i) => (
                    <li key={i}>&ldquo;{row.raw}&rdquo; &mdash; {row.reason}</li>
                  ))}
                  {result.invalid.length > 20 && <li>&hellip; and {result.invalid.length - 20} more</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="pt-3 gap-2 flex-wrap">
          {result ? (
            <>
              <Button type="button" variant="outline" onClick={reset} className="btn-outline-ink rounded-full h-10 px-4">
                Import more
              </Button>
              <Button type="button" onClick={() => { setOpen(false); reset(); }} className="btn-ink rounded-full h-10 px-5">
                Done
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="btn-outline-ink rounded-full h-10 px-4">
                Cancel
              </Button>
              <Button
                type="button"
                onClick={runImport}
                disabled={busy || !text.trim()}
                data-testid="bulk-import-run"
                className="btn-ink rounded-full h-10 px-5"
              >
                {busy ? <Loader2 className="animate-spin" size={14} /> : "Import contacts"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ConversionFunnel({ stats, analytics }) {
  const visitors = analytics?.unique_visitors ?? 0;
  const leads = stats?.leads_total ?? 0;
  const registrations = stats?.total_registrations ?? 0;
  const paid = stats?.by_status?.paid ?? 0;

  const stages = [
    { key: "visitors", label: "Visitors", subtitle: "Unique site visits", value: visitors, color: "bg-ink", textColor: "text-ink" },
    { key: "leads", label: "Leads", subtitle: "Gave us their email", value: leads, color: "bg-brand", textColor: "text-brand" },
    { key: "registrations", label: "Registrations", subtitle: "Started checkout", value: registrations, color: "bg-amber-500", textColor: "text-amber-700" },
    { key: "paid", label: "Paid", subtitle: "Completed checkout", value: paid, color: "bg-emerald-500", textColor: "text-emerald-700" },
  ];
  const maxVal = Math.max(1, ...stages.map((s) => s.value));

  const pct = (cur, prev) => {
    if (!prev || prev === 0) return null;
    const v = (cur / prev) * 100;
    if (!isFinite(v)) return null;
    return v;
  };

  return (
    <div data-testid="conversion-funnel">
      <div className="flex items-center gap-2 mb-4">
        <TrendingDown size={18} className="text-brand" />
        <h2 className="font-display text-xl tracking-[-0.02em]">Conversion funnel</h2>
        <span className="text-xs text-ink/55 font-mono-spec uppercase tracking-wider ml-1">
          Visitors &rarr; Leads &rarr; Registrations &rarr; Paid
        </span>
      </div>

      <div className="rounded-2xl border border-[var(--line)] bg-bone p-5 sm:p-6 space-y-4">
        {stages.map((stage, i) => {
          const widthPct = Math.max(2, Math.round((stage.value / maxVal) * 100));
          const prev = i > 0 ? stages[i - 1].value : null;
          const conv = pct(stage.value, prev);
          const drop = prev != null ? prev - stage.value : null;
          return (
            <div key={stage.key} data-testid={`funnel-stage-${stage.key}`} className="flex flex-col gap-1.5">
              <div className="flex items-end justify-between gap-3 flex-wrap">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-ink/55 font-mono-spec w-4 text-right">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-lg sm:text-xl tracking-[-0.02em] text-ink">{stage.label}</span>
                  <span className="text-xs text-ink/55">{stage.subtitle}</span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className={`font-display text-2xl sm:text-3xl tracking-[-0.025em] ${stage.textColor}`} data-testid={`funnel-count-${stage.key}`}>
                    {stage.value.toLocaleString()}
                  </span>
                  {conv != null && (
                    <span className="text-[11px] font-mono-spec uppercase tracking-wider text-ink/55" data-testid={`funnel-conv-${stage.key}`}>
                      {conv >= 100 ? `+${(conv - 100).toFixed(0)}%` : `${conv.toFixed(1)}%`} of prev
                    </span>
                  )}
                </div>
              </div>
              <div className="relative h-7 rounded-md bg-white border border-[var(--line)] overflow-hidden ml-7">
                <div
                  className={`absolute inset-y-0 left-0 ${stage.color} transition-all duration-500 ease-out`}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
              {drop != null && drop > 0 && conv != null && conv < 100 && (
                <div className="ml-7 text-[11px] text-ink/55">
                  <span className="font-mono-spec uppercase tracking-wider">Drop-off:</span> {drop.toLocaleString()} ({(100 - conv).toFixed(1)}%)
                </div>
              )}
            </div>
          );
        })}

        <div className="pt-4 mt-2 border-t border-[var(--line)] grid grid-cols-2 sm:grid-cols-4 gap-3">
          <FunnelMetric label="Visitor &rarr; Paid" value={pct(paid, visitors)} highlight />
          <FunnelMetric label="Lead &rarr; Paid" value={pct(paid, leads)} />
          <FunnelMetric label="Registration &rarr; Paid" value={pct(paid, registrations)} />
          <FunnelMetric label="Revenue (est.)" value={`$${(paid * 150).toLocaleString()}`} raw />
        </div>
      </div>
    </div>
  );
}

function FunnelMetric({ label, value, highlight, raw }) {
  const display = raw ? value : (value == null ? "—" : `${value.toFixed(1)}%`);
  return (
    <div className={`rounded-xl border p-3 ${highlight ? "border-brand/40 bg-brand-soft" : "border-[var(--line)] bg-white"}`}>
      <div className="text-[10px] uppercase tracking-wider text-ink/55 font-mono-spec">{label}</div>
      <div className={`font-display text-xl tracking-[-0.02em] mt-0.5 ${highlight ? "text-brand" : "text-ink"}`}>{display}</div>
    </div>
  );
}
